import { useEffect, useState } from "react";
import { FaRegTrashAlt } from "react-icons/fa";
import ParcelService from "../../../services/parcelService";
import { FiPlus } from 'react-icons/fi';
import { FormModal } from "../../layout/Modal/Modal";
import { SeedTask } from "../../../interfaces/SeedTask";
import SeedCreateForm from "./SeedCreateForm";
import IconButton from "../../common/IconButton";
import { formatDateDisplay } from "../../../utils/date";


interface FormProps {
    parcelId: number
    cycleId: number
    cycleCompleted?: boolean
}

function SeedForm(props: FormProps) {
    const parcelService = new ParcelService();


    const [data, setData] = useState<SeedTask[]>([]);
    const [showModal, setShowModal] = useState(false);
    const [showDetail, setShowDetail] = useState(false);
    const [selectedTask, setSelectedTask] = useState<SeedTask | null>(null);
    const [loading, setLoading] = useState(true);


    const fetchSeedTasks = async () => {
        setLoading(true);
        try {
            const response = await parcelService.getSeedTasks(props.parcelId, props.cycleId);

            const processedData = response.map((task: SeedTask) => ({
                ...task,
                date: new Date(task.date), // Convert date string to Date object
            }));
            setData(processedData);
        } catch (error) {
            console.error("Error fetching seed tasks:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSeedTasks();
    }, [props.parcelId]);

    const toggleModal = () => {
        setShowModal((prev) => !prev);
    };

    const toggleDetail = (task?: SeedTask) => {
        setSelectedTask(task || null);
        setShowDetail((prev) => !prev);
    };


    const handleDelete = (id: string) => {
        parcelService.deleteSeedTask(props.parcelId, props.cycleId, Number(id))
        setData((prevData) => prevData.filter(entry => entry.id !== id));
    }

    const updateRow = (updated: SeedTask) => {
        setData(prev => prev.map(e => e.id === updated.id ? updated : e));
        setShowDetail(false);
    };

    const getTotalAmount = () => {
        return data.reduce((total, row) => total + (Number(row.quantity) || 0), 0);
    }


    return (
        <div className="table-container px-1 md:px-5">
            <h1 className="text-xl font-bold pb-3 text-gray-800">Saat</h1>
            {loading && (
                <div className="progress-container !h-2">
                    <div className="progress-bar"></div>
                </div>
            )}
            <div className="table-wrapper hidden md:block">
                <table>
                    <thead>
                        <tr>
                            <th>Datum</th>
                            <th>Sorte</th>
                            <th>Menge/ha</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.length === 0 && <tr>
                            <td colSpan={4} style={{ textAlign: "center" }}>Keine Einträge</td>
                        </tr>}
                        {data.map((row, index) => (
                            <tr key={index} onClick={() => toggleDetail(row)}>
                                <td>{formatDateDisplay(row.date)}</td>
                                <td>{row.kind}</td>
                                <td>{(row.quantity)}</td>
                                <td>
                                    <span className="icon-span" onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(row.id)
                                    }}>
                                        <FaRegTrashAlt />
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="py-2">Total (pro/ha): <b>{getTotalAmount()}</b></p>
            </div>

            <div className='flex flex-1 flex-col w-full gap-3 md:hidden'>
                {data.map((row, index) =>
                    <div key={index} className='flex flex-1 w-full gap-5 flex-col justify-between shadow-xl border-2 rounded-lg py-4 px-3 border-gray-300' onClick={() => toggleDetail(row)}>
                        <div className='flex flex-1 flex-col gap-1'>
                            <div className='flex flex-1 gap-2 items-center'>
                                <h3 className='font-semibold text-base'>Art: </h3>
                                <p className='text-base text-gray-600'>{row.kind}</p>
                            </div>
                            <div className='flex flex-1 gap-2 items-center'>
                                <h3 className='font-semibold text-base'>Menge/ha: </h3>
                                <p className='text-base text-gray-600'>{row.quantity}</p>
                            </div>
                        </div>
                        <div className='flex flex-1 justify-between'>
                            <div className='flex flex-1 gap-2 items-center'>
                                <h3 className='font-semibold text-base'>Datum: </h3>
                                <p className="text-base text-gray-600">{formatDateDisplay(row.date!)}</p>
                            </div>
                            <button className="icon-span cursor-pointer" onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(row.id)
                            }}>
                                <FaRegTrashAlt color="#ef4444" />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            <div className='flex flex-1 w-full flex-col md:hidden items-start pt-4 mb-8'>
                {data.length === 0 && <p className='text-center mb-0'>Keine Einträge</p>}
                <p className="py-2">Total (pro/ha): <b>{getTotalAmount()}</b></p>
            </div>

            <IconButton className="add-btn"
                icon={<FiPlus size={26} />}
                onClick={() => toggleModal()}
            />

            <FormModal open={showModal} onClose={toggleModal} size="xl">
                <SeedCreateForm parcelId={props.parcelId} cycleId={props.cycleId} onSave={() => { fetchSeedTasks(); toggleModal(); }} isNew={true}></SeedCreateForm>
            </FormModal>

            <FormModal open={showDetail} onClose={toggleDetail} size="xl">
                <SeedCreateForm parcelId={props.parcelId} cycleId={props.cycleId} onSave={updateRow} isNew={false} task={selectedTask!} readOnly={props.cycleCompleted}></SeedCreateForm>
            </FormModal>
        </div>
    )
}

export default SeedForm;